import * as React from 'react'
import { useDispatch } from 'react-redux'
import { setIsStored } from '../../../redux/slices/Alerts'
import {
  Box,
  Paper,
  Divider,
  Grid,
  Typography,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { datasetFormik } from './datasetFormik'
import { Formik, Form, useFormikContext } from 'formik'
import BottonInside from '../../BottonCustom/BottonInside'
import FormikTextfield from '../../FormsUI/Textfield'
import * as service from '../../../services'
import { fetchEnabledTenants } from '../../../services/tenantService'
import Modal from '../../FormCreationModal'

const gridStyle = { mt: 0, width: '100%', ml: 0, pr: 2 }

const FormikDatePicker = ({ name, label, minDate }) => {
  const { values, setFieldValue, errors, touched } = useFormikContext()
  const hasError = Boolean(touched[name] && errors[name])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimePicker
        label={label}
        value={values[name]}
        minDateTime={minDate}
        inputFormat="dd/MM/yyyy HH:mm"
        onChange={value => setFieldValue(name, value)}
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            variant="outlined"
            error={hasError}
            helperText={hasError ? errors[name] : ''}
            inputProps={{ ...params.inputProps, readOnly: true }}
          />
        )}
      />
    </LocalizationProvider>
  )
}

const PublicEventEditModal = (props, ref) => {
  const dispatch = useDispatch()
  const [open, setOpen] = React.useState(false)
  const [eventData, setEventData] = React.useState(null)

  const [users, setUsers] = React.useState([])
  const [tenants, setTenants] = React.useState([])
  const [loadingOptions, setLoadingOptions] = React.useState(false)
  const [selectedUser, setSelectedUser] = React.useState(null)

  const { FORM_VALIDATION } = React.useMemo(() => datasetFormik(), [])

  const handleCancel = () => {
    setSelectedUser(null)
    setEventData(null)
    setOpen(false)
  }

  React.useImperativeHandle(ref, () => ({
    openModal: row => {
      setEventData(row)
      setOpen(true)
    },
  }))

  React.useEffect(() => {
    if (!open || !eventData) return
    setLoadingOptions(true)
    Promise.all([service.getUserAll(), fetchEnabledTenants()])
      .then(([allUsers, activeTenants]) => {
        const enabledUsers = (allUsers || []).filter(u => u.habilitado)
        setUsers(enabledUsers)
        setTenants(activeTenants || [])
        const match = enabledUsers.find(u => u.id === eventData.userId)
        setSelectedUser(match || null)
      })
      .finally(() => setLoadingOptions(false))
  }, [open, eventData])

  const handleSubmit = async values => {
    const { code, descripcion, userId, tenantId, startDate, endDate } = values
    const data = {
      code: code.trim().toUpperCase(),
      descripcion: descripcion || null,
      userId,
      tenantId: parseInt(tenantId, 10),
      startDate,
      endDate,
    }

    const response = await service.updatePublicEvent(data, eventData.id)
    const itsStored =
      response?.action === 'updated'
        ? { stored: true }
        : { stored: false, message: response?.message || 'Error al actualizar el evento.' }

    handleCancel()
    dispatch(setIsStored(itsStored))
  }

  if (!eventData) return null

  const isCodeLocked = eventData.isActive === true

  const initialValues = {
    code: eventData.code || '',
    descripcion: eventData.descripcion || '',
    userId: eventData.userId || '',
    tenantId: eventData.tenantId || '',
    startDate: eventData.startDate ? new Date(eventData.startDate) : null,
    endDate: eventData.endDate ? new Date(eventData.endDate) : null,
  }

  return (
    <Modal
      open={open}
      title="Editar evento de Prueba"
      description="Modifica los datos del evento público.">
      <Box>
        <Formik
          initialValues={initialValues}
          validationSchema={FORM_VALIDATION}
          onSubmit={handleSubmit}
          enableReinitialize>
          {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
            <Form>
              <Paper style={{ height: '100%', overflow: 'auto', borderRadius: 0 }}>
                <Divider />
                <Grid container spacing={2} sx={{ ...gridStyle, px: 2, pt: 2, pb: 1 }}>
                  <Grid item xs={6}>
                    <Tooltip
                      title={
                        isCodeLocked
                          ? 'Para cambiar el código, el evento debe estar Desactivado'
                          : ''
                      }
                      placement="top"
                      arrow>
                      <span>
                        <FormikTextfield
                          name="code"
                          label="Código de acceso"
                          variant="outlined"
                          InputProps={{ readOnly: isCodeLocked }}
                        />
                      </span>
                    </Tooltip>
                  </Grid>

                  {/* Descripción */}
                  <Grid item xs={6}>
                    <FormikTextfield
                      name="descripcion"
                      label="Descripción de la prueba"
                      variant="outlined"
                    />
                  </Grid>

                  {/* Usuario designado */}
                  <Grid item xs={12}>
                    {loadingOptions ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                          Cargando opciones...
                        </Typography>
                      </Box>
                    ) : (
                      <Autocomplete
                        options={users}
                        getOptionLabel={u => `${u.email} — ${u.nombre} ${u.apellido}`}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        value={selectedUser}
                        onChange={(_, newValue) => {
                          setSelectedUser(newValue)
                          setFieldValue('userId', newValue?.id ?? '')
                        }}
                        onBlur={() => setFieldTouched('userId', true)}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Usuario designado"
                            variant="outlined"
                            error={Boolean(touched.userId && errors.userId)}
                            helperText={touched.userId && errors.userId}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  {/* Tenant */}
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      error={Boolean(touched.tenantId && errors.tenantId)}>
                      <InputLabel>Ciudad / Tenant</InputLabel>
                      <Select
                        label="Ciudad / Tenant"
                        value={values.tenantId}
                        disabled={loadingOptions}
                        onChange={e => setFieldValue('tenantId', e.target.value)}
                        onBlur={() => setFieldTouched('tenantId', true)}>
                        {tenants.map(t => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.name} — {t.fullName}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.tenantId && errors.tenantId && (
                        <FormHelperText>{errors.tenantId}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Fechas */}
                  <Grid item xs={6}>
                    <FormikDatePicker name="startDate" label="Fecha inicio" />
                  </Grid>
                  <Grid item xs={6}>
                    <FormikDatePicker
                      name="endDate"
                      label="Fecha fin"
                      minDate={values.startDate || undefined}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ pt: 1 }} />
                <BottonInside enableButton={true} hdleButtonCancel={handleCancel} />
              </Paper>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  )
}

export default React.forwardRef(PublicEventEditModal)
